import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import type { MemberType } from '../MemberProfile.types'

import { Flex, Badge, UserAvatar, CardDashboard, Typography } from '@percent/lemonade'
import type { Account } from '@percent/domain/iam'

type ProfileHeaderProps = {
  account: Account
  memberType: MemberType
  actions?: ReactNode
}

export function ProfileHeader({ account, memberType, actions }: ProfileHeaderProps) {
  const { t } = useTranslation()
  const displayName = account.fullName || account.email
  const initials = account.fullName
    ? account.fullName
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : (account.email?.[0] ?? '?').toUpperCase()

  return (
    <CardDashboard
      // Identity goes in the title slot and the lifecycle buttons in the
      // `action` slot, so CardDashboard lays them out (title left, actions
      // right) — matching the other profile cards (AdminCard, security, etc.)
      // rather than a hand-rolled flex.
      title={
        <Flex direction="row" align="center" gap="md">
          <UserAvatar name={initials} size={40} radius={4} />
          <Flex direction="column" gap="xxs">
            <Flex direction="row" align="center" gap="sm">
              <Typography variant="h4">{displayName}</Typography>
              {(memberType === 'partner' || memberType === 'organisation') && (
                <Badge variant="default">{account.role?.name ?? t('typography.member')}</Badge>
              )}
              {account.ssoUser && <Badge variant="informative">{t('memberProfile.scim')}</Badge>}
              {account.status === 'removed' && <Badge variant="critical">{t('status.removed')}</Badge>}
            </Flex>
            <Typography variant="default" $color="neutral300">
              {account.email}
            </Typography>
          </Flex>
        </Flex>
      }
      action={actions}
    />
  )
}
